import { create } from 'zustand';
import type { FileItem } from './fileStore';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning' | 'loading';
  title: string;
  message?: string;
  duration?: number;
}

interface UIState {
  // Sidebar
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;

  // Modals
  isCreateFolderModalOpen: boolean;
  isRenameModalOpen: boolean;
  isCreateLinkModalOpen: boolean;
  isDeleteConfirmOpen: boolean;

  // Context
  contextMenuTarget: { id: string; type: 'file' | 'folder' } | null;
  contextMenuPosition: { x: number; y: number } | null;

  // Toasts
  toasts: Toast[];

  // Upload
  isUploadModalOpen: boolean;
  uploadProgress: Map<string, number>;
  pendingUploadFiles: File[];

  // Preview
  previewFile: FileItem | null;

  // Move modal
  isMoveModalOpen: boolean;
  moveModalFileIds: string[];

  // Create document modal
  isCreateDocumentModalOpen: boolean;

  // Actions
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  setSidebarOpen: (open: boolean) => void;

  openCreateFolderModal: () => void;
  closeCreateFolderModal: () => void;

  openRenameModal: () => void;
  closeRenameModal: () => void;

  openCreateLinkModal: () => void;
  closeCreateLinkModal: () => void;

  openDeleteConfirm: () => void;
  closeDeleteConfirm: () => void;

  setContextMenuTarget: (target: { id: string; type: 'file' | 'folder' } | null) => void;
  openContextMenu: (target: { id: string; type: 'file' | 'folder' }, position: { x: number; y: number }) => void;
  closeContextMenu: () => void;
  clearContextMenuTarget: () => void;

  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;

  openUploadModal: (files?: File[]) => void;
  closeUploadModal: () => void;
  setUploadProgress: (fileId: string, progress: number) => void;
  clearUploadProgress: (fileId: string) => void;
  clearPendingUploadFiles: () => void;

  setPreviewFile: (file: FileItem | null) => void;

  openMoveModal: (fileIds: string[]) => void;
  closeMoveModal: () => void;

  openCreateDocumentModal: () => void;
  closeCreateDocumentModal: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  isSidebarOpen: true,
  isSidebarCollapsed: false,

  isCreateFolderModalOpen: false,
  isRenameModalOpen: false,
  isCreateLinkModalOpen: false,
  isDeleteConfirmOpen: false,

  contextMenuTarget: null,
  contextMenuPosition: null,

  toasts: [],

  isUploadModalOpen: false,
  uploadProgress: new Map(),
  pendingUploadFiles: [],

  previewFile: null,

  isMoveModalOpen: false,
  moveModalFileIds: [],

  isCreateDocumentModalOpen: false,

  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
  toggleSidebarCollapse: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  setSidebarOpen: (open) => set({ isSidebarOpen: open }),

  openCreateFolderModal: () => set({ isCreateFolderModalOpen: true }),
  closeCreateFolderModal: () => set({ isCreateFolderModalOpen: false }),

  openRenameModal: () => set({ isRenameModalOpen: true }),
  closeRenameModal: () => set({ isRenameModalOpen: false, contextMenuTarget: null }),

  openCreateLinkModal: () => set({ isCreateLinkModalOpen: true }),
  closeCreateLinkModal: () => set({ isCreateLinkModalOpen: false, contextMenuTarget: null }),

  openDeleteConfirm: () => set({ isDeleteConfirmOpen: true }),
  closeDeleteConfirm: () => set({ isDeleteConfirmOpen: false, contextMenuTarget: null }),

  setContextMenuTarget: (target) => set({ contextMenuTarget: target }),

  openContextMenu: (target, position) =>
    set({ contextMenuTarget: target, contextMenuPosition: position }),

  closeContextMenu: () =>
    set({ contextMenuPosition: null }),

  clearContextMenuTarget: () =>
    set({ contextMenuTarget: null }),

  addToast: (toast) => {
    const id = crypto.randomUUID();
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    return id;
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearAllToasts: () => set({ toasts: [] }),

  openUploadModal: (files?: File[]) => set({ isUploadModalOpen: true, pendingUploadFiles: files || [] }),
  closeUploadModal: () => set({ isUploadModalOpen: false, pendingUploadFiles: [] }),
  clearPendingUploadFiles: () => set({ pendingUploadFiles: [] }),

  setUploadProgress: (fileId, progress) =>
    set((state) => {
      const newProgress = new Map(state.uploadProgress);
      newProgress.set(fileId, progress);
      return { uploadProgress: newProgress };
    }),

  clearUploadProgress: (fileId) =>
    set((state) => {
      const newProgress = new Map(state.uploadProgress);
      newProgress.delete(fileId);
      return { uploadProgress: newProgress };
    }),

  setPreviewFile: (file) => set({ previewFile: file }),

  openMoveModal: (fileIds) => set({ isMoveModalOpen: true, moveModalFileIds: fileIds }),
  closeMoveModal: () => set({ isMoveModalOpen: false, moveModalFileIds: [] }),

  openCreateDocumentModal: () => set({ isCreateDocumentModalOpen: true }),
  closeCreateDocumentModal: () => set({ isCreateDocumentModalOpen: false }),
}));
